import { ulid } from '@vp/core';
import { ACTIVE_KEY } from './local';

/**
 * The device's list of profiles (docs/TZ-PROFILES.md): IndexedDB `vp-device`, readable before any
 * profile is open. A record holds the card (name, picture, logo), where the profile's data lives
 * (its own IndexedDB database, and a folder if chosen), the PIN (salt and hash only) with its failed
 * attempts, and what a future sync needs. The active id is also mirrored into `localStorage` so it is
 * known synchronously and other tabs hear about a switch.
 */
export const MAX_PROFILES = 10;
export const LOCK_AFTER = [0, 5, 15, 60] as const;
export type LockAfter = (typeof LOCK_AFTER)[number];

export interface ProfilePin {
  salt: string;
  hash: string;
  iterations: number;
}

/**
 * Where the canvases are: the profile's database, or a folder. `own` — the folder is the profile's
 * (data at its root); `shared` — a folder of profiles, this one's data in the subfolder named by its id.
 */
export type ProfileStorage = { kind: 'browser' } | { kind: 'folder'; mode: 'own' | 'shared'; handle: FileSystemDirectoryHandle; name: string };

export interface ProfileRecord {
  id: string;
  firstName: string;
  lastName: string;
  /** A small picture (data: URL, 256 px), '' — initials. */
  avatar: string;
  /** The mark on the home header (data: URL) or ''. */
  logo: string;
  createdAt: number;
  lastUsedAt: number;
  /** Its IndexedDB database (`vp-p-<id>`; `vp` for the profile made from the data before profiles). */
  db: string;
  storage: ProfileStorage;
  pin: ProfilePin | null;
  /** Lock after so many idle minutes (0 — never). */
  lockAfter: LockAfter;
  /** Wrong PINs in a row and until when the next try waits (survive reloads). */
  failed: number;
  lockedUntil: number;
  /** A future sync: whether the card changed here since the cloud copy. */
  sync: { state: 'pending' | 'acked'; version: number };
  /** A future account the profile is linked to. */
  account: null;
}

const DB = 'vp-device';
const PROFILES = 'profiles';
const META = 'meta';

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(PROFILES)) db.createObjectStore(PROFILES, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'key' });
    };
    req.onsuccess = () => {
      const db = req.result;
      // another tab deletes or upgrades the registry: let it, open again next time
      db.onversionchange = () => {
        db.close();
        opening = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('the profile list cannot be opened'));
    req.onblocked = () => reject(new Error('the profile list is blocked by another tab'));
  }).catch((e: unknown) => {
    opening = null;
    throw e;
  });
  return opening;
}

function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest | void): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const req = fn(tx.objectStore(store));
        tx.oncomplete = () => resolve((req ? req.result : undefined) as T);
        tx.onerror = () => reject(tx.error ?? new Error('profile list write failed'));
        tx.onabort = () => reject(tx.error ?? new Error('profile list write aborted'));
      }),
  );
}

/** Can the registry be kept at all (IndexedDB present and writable)? */
export async function registryAvailable(): Promise<boolean> {
  try {
    if (typeof indexedDB === 'undefined' || !indexedDB) return false;
    await run(META, 'readwrite', (s) => s.put({ key: '__probe', value: Date.now() }));
    await run(META, 'readwrite', (s) => s.delete('__probe'));
    return true;
  } catch {
    return false;
  }
}

/** Profiles, the last used first. */
export async function listProfiles(): Promise<ProfileRecord[]> {
  const all = await run<ProfileRecord[]>(PROFILES, 'readonly', (s) => s.getAll());
  return all.sort((a, b) => b.lastUsedAt - a.lastUsedAt);
}

export async function getProfile(id: string): Promise<ProfileRecord | undefined> {
  return (await run<ProfileRecord | undefined>(PROFILES, 'readonly', (s) => s.get(id))) ?? undefined;
}

export async function putProfile(p: ProfileRecord): Promise<void> {
  await run(PROFILES, 'readwrite', (s) => s.put(p));
}

/** Change some fields of a profile (the card: the sync state becomes pending). */
export async function updateProfile(id: string, patch: Partial<ProfileRecord>, card = false): Promise<ProfileRecord> {
  const p = await getProfile(id);
  if (!p) throw new Error('no such profile');
  const next: ProfileRecord = { ...p, ...patch };
  if (card) next.sync = { state: 'pending', version: p.sync.version + 1 };
  await putProfile(next);
  return next;
}

export async function removeProfileRecord(id: string): Promise<void> {
  await run(PROFILES, 'readwrite', (s) => s.delete(id));
  if ((await getActiveId()) === id) await setActiveId(null);
}

export async function getActiveId(): Promise<string | null> {
  const row = await run<{ value?: string } | undefined>(META, 'readonly', (s) => s.get('active'));
  return typeof row?.value === 'string' ? row.value : null;
}

/** The active profile for every tab: kept in the registry and mirrored for synchronous readers. */
export async function setActiveId(id: string | null): Promise<void> {
  await run(META, 'readwrite', (s) => (id ? s.put({ key: 'active', value: id }) : s.delete('active')));
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* storage blocked: the registry still knows */
  }
}

export interface NewProfile {
  firstName: string;
  lastName?: string;
  avatar?: string;
  logo?: string;
  pin?: ProfilePin | null;
  /** The database to use (the profile made from older data keeps `vp`). */
  db?: string;
  storage?: ProfileStorage;
}

export class ProfileLimitError extends Error {
  constructor() {
    super(`no more than ${MAX_PROFILES} profiles on a device`);
  }
}

/** A new profile record (saved); its database is created when it is first opened. */
export async function createProfile(n: NewProfile): Promise<ProfileRecord> {
  const name = n.firstName.trim();
  if (!name) throw new Error('the first name is needed');
  if ((await listProfiles()).length >= MAX_PROFILES) throw new ProfileLimitError();
  const id = ulid();
  const now = Date.now();
  const p: ProfileRecord = {
    id,
    firstName: name.slice(0, 80),
    lastName: (n.lastName ?? '').trim().slice(0, 80),
    avatar: n.avatar ?? '',
    logo: n.logo ?? '',
    createdAt: now,
    lastUsedAt: now,
    db: n.db ?? `vp-p-${id}`,
    storage: n.storage ?? { kind: 'browser' },
    pin: n.pin ?? null,
    lockAfter: 0,
    failed: 0,
    lockedUntil: 0,
    sync: { state: 'pending', version: 1 },
    account: null,
  };
  await putProfile(p);
  return p;
}

/** "Anna Petrova" / "Anna". */
export function displayName(p: Pick<ProfileRecord, 'firstName' | 'lastName'>): string {
  return [p.firstName, p.lastName].map((s) => s.trim()).filter(Boolean).join(' ');
}

/** Initials for a profile without a picture. */
export function initials(p: Pick<ProfileRecord, 'firstName' | 'lastName'>): string {
  const a = p.firstName.trim()[0] ?? '';
  const b = p.lastName.trim()[0] ?? '';
  return (a + b).toUpperCase() || '?';
}

const AVATAR_COLORS = ['#5e9cff', '#a78bfa', '#f472b6', '#fb923c', '#34d399', '#22d3ee', '#facc15', '#f87171'];
/** A stable colour per profile for the initials circle. */
export function avatarColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
